import { leadsApiBaseUrl, environment } from '$lib/config';
import { ApiError } from '$shared/lib/api/errors';

/**
 * Contact form request - matches backend ContactRequest model
 * Uses PascalCase for JSON serialization to .NET backend
 */
export interface ContactRequest {
	Email: string;
	Name?: string;
	Subject?: string;
	Message?: string;
	Source?: string;
}

/**
 * API response from lead submission endpoints
 */
export interface LeadResponse {
	Success: boolean;
	Message: string;
	LeadId: string | null;
}

/**
 * Error thrown when API returns a non-success response
 */
export class LeadApiError extends Error {
	constructor(
		message: string,
		public statusCode: number,
		public isRateLimited: boolean = false,
		/** Field-keyed messages from an RFC 7807 `ValidationProblemDetails` 400, if the API sent one. */
		public validationErrors?: Record<string, string[]>
	) {
		super(message);
		this.name = 'LeadApiError';
	}
}

/**
 * Turn a failed response into a LeadApiError carrying a message fit to show a user.
 *
 * The previous behaviour piped `response.text()` straight into the message, which put a raw
 * `{"type":"https://tools.ietf.org/...","errors":{...}}` blob in front of anyone who mistyped
 * their email. `ApiError.fromResponse` already parses the .NET Problem Details these endpoints
 * return, so lean on it rather than growing a second parser; the wrapper exists only because
 * callers depend on `LeadApiError`'s `isRateLimited`.
 *
 * Titles are deliberately not used: they're generic by construction ("Bad Request", "One or
 * more validation errors occurred.") and the caller's fallback is better copy.
 */
async function toLeadApiError(response: Response, fallback: string): Promise<LeadApiError> {
	const apiError = await ApiError.fromResponse(response);
	const message = apiError.allValidationMessages[0] ?? apiError.detail ?? fallback;

	return new LeadApiError(message, response.status, false, apiError.validationErrors);
}

/**
 * Submit a contact form to the leads API
 *
 * @param data Contact form data (Email required, others optional)
 * @returns LeadResponse on success
 * @throws LeadApiError on API errors (including rate limiting)
 */
export async function submitContact(
	data: Omit<ContactRequest, 'Source'>
): Promise<LeadResponse> {
	const request: ContactRequest = {
		...data,
		Source: `marketing-${environment}`
	};

	const response = await fetch(`${leadsApiBaseUrl}/api/leads/contact`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify(request)
	});

	if (response.status === 429) {
		throw new LeadApiError(
			'Too many requests. Please wait a moment and try again.',
			429,
			true
		);
	}

	if (!response.ok) {
		throw await toLeadApiError(response, 'Failed to submit contact form. Please try again.');
	}

	return response.json();
}

/**
 * Demo request - matches backend DemoRequest model
 * Uses PascalCase for JSON serialization to .NET backend
 */
export interface DemoRequest {
	Email: string;
	Name?: string;
	Company?: string;
	UseCase?: string;
	TeamSize?: string;
	Source?: string;
}

/**
 * Submit a demo request to the leads API
 *
 * @param data Demo request data (Email required, others optional)
 * @returns LeadResponse on success
 * @throws LeadApiError on API errors (including rate limiting)
 */
export async function submitDemo(data: Omit<DemoRequest, 'Source'>): Promise<LeadResponse> {
	const request: DemoRequest = {
		...data,
		Source: 'marketing-site-demo'
	};

	const response = await fetch(`${leadsApiBaseUrl}/api/leads/demo`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify(request)
	});

	if (response.status === 429) {
		throw new LeadApiError('Too many requests. Please wait a moment and try again.', 429, true);
	}

	if (!response.ok) {
		throw await toLeadApiError(response, 'Failed to submit demo request. Please try again.');
	}

	return response.json();
}

/**
 * Newsletter subscription request
 */
export interface NewsletterRequest {
	Email: string;
	Source?: string;
}

/**
 * Subscribe an email to the newsletter
 *
 * @param email Email address to subscribe
 * @returns LeadResponse on success
 * @throws LeadApiError on API errors (including rate limiting)
 */
export async function submitNewsletter(email: string): Promise<LeadResponse> {
	const request: NewsletterRequest = {
		Email: email,
		Source: `marketing-${environment}`
	};

	const response = await fetch(`${leadsApiBaseUrl}/api/leads/newsletter`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify(request)
	});

	if (response.status === 429) {
		throw new LeadApiError(
			'Too many requests. Please wait a moment and try again.',
			429,
			true
		);
	}

	if (!response.ok) {
		throw await toLeadApiError(response, 'Failed to subscribe to newsletter. Please try again.');
	}

	return response.json();
}
